import { 
  createContext, 
  useCallback,
  useContext, 
  useEffect,
  useMemo, 
  useState 
} from 'react';
import { useAlert } from 'components/provider/Alert';
import { useHelloApi } from 'components/provider/HelloApi';
import { withRouter } from 'react-router';
import Api from 'api';
import Sentry from 'sentry';

export const AuthContext = createContext();

function AuthProvider ({ 
  children, 
  history, 
}) {

  const { alertFailure, clearAlert } = useAlert();
  const [ redirectTo, setLoginRedirectTo ] = useState(null);
  const [ isLoggingIn, setIsLoggingIn ] = useState(false);
  const [ isFetchingAccount, setIsFetchingAccount ] = useState(false);
  const [ accounts, setAccounts ] = useState(null);
  
  const { 
    isCheckingForMaintenanceMode, 
    isInMaintenanceMode 
  } = useHelloApi();

  const account = useMemo(() => 
    accounts === null 
      ? undefined
      : accounts.find(a => a.isActive) || null, 
    [accounts]
  );
  const exposedToken = useMemo(() => account?.token, [account]);

  function filterDuplicatesPreferActiveThenLast(a, key) {
    const map = new Map();

    for (const item of a) {
      const k = key(item);
      const existing = map.get(k);

      if (!existing) {
        // first time we see this key
        map.set(k, item);
      } else if (item.isActive && !existing.isActive) {
        // prefer active over inactive
        map.set(k, item);
      } else if (item.isActive === existing.isActive) {
        // if both have same active status, prefer the later one
        map.set(k, item);
      }
      // else keep the existing active one
    }

    return [...map.values()];
  }

  const fetchToken = useCallback(
		async({ email, password }) => {
      try {
        clearAlert();
        const loginResponse = await Api.postForm('/auth/login/', null, {
          username: email,
          password
        });
        return loginResponse?.data?.data?.attributes?.auth_token;  
      } catch (e) {
        alertFailure("Login failed");
        Sentry.captureException(e);
        return null;
      }
    },
		[
      clearAlert,
      alertFailure,
    ]
	);

  const fetchAccount = useCallback(
    async token => {
      try {
        // get the account
        const accountResponse = await Api.get('/accounts/me/', token, {
          include: 'auth_user,youtube_access_tokens,properties'
        }, null, true);
      
        const patreonAccessTokens = accountResponse?.data?.included?.filter(i => i.type === 'PatreonToken');
        const youtubeAccessTokens = accountResponse?.data?.included?.filter(i => i.type === 'YoutubeToken');

        const isStaff = accountResponse?.data?.data?.type === 'User' && accountResponse?.data?.included.some(i => i.attributes.is_staff);
        const isSponsor = accountResponse?.data?.data?.type === 'SponsorUser';
        const isCreator = accountResponse?.data?.data?.type === 'User' && !isStaff;
        const sponsors = (isSponsor && accountResponse?.data?.data?.relationships?.sponsors?.data) || [];
        const sysAdminIds = ['111', '243'];
        const properties = accountResponse?.data?.included?.filter(i => i.type === 'Property') || [];
        const account = {
          id: accountResponse?.data?.data?.id,
          auth_user: accountResponse?.data?.data?.relationships?.auth_user?.data?.id,
          isReadOnlyCreator: accountResponse?.data?.included.some(i => i.attributes.read_only),
          isStaff,
          isSponsor,
          isCreator,
          isSysAdmin: sysAdminIds.includes(accountResponse?.data?.data?.id),
          hasConnectedYoutube: youtubeAccessTokens && youtubeAccessTokens.length > 0,
          hasConnectedPatreon: patreonAccessTokens && patreonAccessTokens.length > 0,
          patreonAccessTokens,
          sponsors,
          youtubeAccessTokens,
          properties: properties.map(p => ({
            id: p.id,
            ...p.attributes
          })),
          propertyCount: properties.length,
          ...accountResponse?.data?.data?.attributes
        }
          
        return account;
      } catch (e) {
        console.error(e);
        Sentry.captureException(e);
        throw e;
      }
    },
    []
  );

  function resetStoredAccounts() {
    window?.localStorage?.setItem('accounts', JSON.stringify([]));
  }

  // retrieve accounts stored in localStorage, while handling and empty or malformed result
  const retrieveStoredAccounts = useCallback(
    () => {
      let result = [];
      const storedAccountsJSON = window?.localStorage?.getItem('accounts');
      if (!storedAccountsJSON) {
        console.log('Stored accounts not found in storage. Initializing stored accounts.');
        resetStoredAccounts();
        return result;
      }
      try {
        result = JSON.parse(storedAccountsJSON);
      } catch (e) {
        console.log('Stored accounts is invalid JSON. Resetting stored accounts.');
        resetStoredAccounts();
        return result;
      }
      if (!Array.isArray(result)) {
        console.log('Stored accounts is not an array. Resetting stored accounts.');
        resetStoredAccounts();
        return [];
      }
      return result;
    },
    []
  );

  const addAccountToStore = useCallback(
    (account) => {
      if (!account) throw new Error('Could not store account - no account details provided.');

      let storedAccounts = retrieveStoredAccounts();

      const newStoredAccounts = storedAccounts
        .map(a => account.isActive ? Object.assign({}, a, { isActive: false }) : a)
        .concat(account);
      const uniqNewStoredAccounts = filterDuplicatesPreferActiveThenLast(newStoredAccounts, acc => acc?.data?.id);
      window?.localStorage?.setItem('accounts', JSON.stringify(uniqNewStoredAccounts));

      return uniqNewStoredAccounts;
    },
    [retrieveStoredAccounts]
  );

  const updateStoredAccount = useCallback(
    (token, patch) => {
      let storedAccounts = retrieveStoredAccounts();
      const newStoredAccounts = storedAccounts.map(a => 
        a.token === token ? Object.assign({}, a, patch) : a
      );
      window?.localStorage?.setItem('accounts', JSON.stringify(newStoredAccounts));
      return newStoredAccounts;
    },
    [retrieveStoredAccounts]
  );

  const removeAccountFromStore = useCallback(
    token => {
      if (!token) throw new Error('Could not remove account - no token provided.');

      let storedAccounts = retrieveStoredAccounts();

      const isTokenStored = storedAccounts.some(a => a.token === token);
      if (!isTokenStored) return storedAccounts;

      const filtered = storedAccounts.filter(a => a.token !== token);
      const newStoredAccounts = filtered.map((a, i) => {
        if (i === 0) {
          return Object.assign({}, a, { isActive: true });
        } else {
          return Object.assign({}, a, { isActive: false });
        }
      });
      window?.localStorage?.setItem('accounts', JSON.stringify(newStoredAccounts));

      return newStoredAccounts;
    },
    [retrieveStoredAccounts]
  );

  const wait = ms => new Promise(resolve => setTimeout(resolve, ms));

  const fetchAndStoreAccount = useCallback(
    async({
      token = null,
      makeActive = false, // whether to set the fetched account as the active account
      doRedirect = false,
      maxRetries = 2
    }) => {
      if (!token) return null;
      if (isInMaintenanceMode || isCheckingForMaintenanceMode) return null;

      setIsFetchingAccount(true);
      let attempt = 0;
      let lastError = null;

      while (attempt <= maxRetries) {
        try {
          const accountData = await fetchAccount(token);
          if (accountData) {
            const account = { token, data: accountData, isActive: makeActive, isStale: false };
            const storedAccounts = addAccountToStore(account);
            setAccounts(storedAccounts);
          }
          lastError = null;
          break;
        } catch (e) {
          lastError = e;
          const status = e?.response?.status;

          if (status === 401 || status === 403) {
            const storedAccounts = removeAccountFromStore(token);
            setAccounts(storedAccounts);
            break;
          }

          if (attempt === maxRetries) {
            const storedAccounts = updateStoredAccount(token, { isStale: true });
            setAccounts(storedAccounts);
            break;
          }

          // compute delay first, then call the shared helper
          const delay = 300 * Math.pow(2, attempt);
          await wait(delay);
        } finally {
          attempt += 1;
        }
      }

      if (!lastError && doRedirect && redirectTo) {
        history.replace(redirectTo);
        setLoginRedirectTo(null);
      }

      setIsFetchingAccount(false);
      return null;
    },
    [
      addAccountToStore, 
      fetchAccount, 
      history,
      isCheckingForMaintenanceMode,
      isInMaintenanceMode,
      redirectTo,
      removeAccountFromStore,
      setAccounts,
      setIsFetchingAccount,
      updateStoredAccount
    ]
  );
  
  const login = useCallback(({ email, password, redirectLocation }) => {
    if (redirectLocation) setLoginRedirectTo(redirectLocation);

    setIsLoggingIn(true);

    // perform the login
    (async () => {
      try {
        const token = await fetchToken({ email, password });
        if (!token) return;

        await fetchAndStoreAccount({
          token,
          makeActive: true,
          doRedirect: true,
        });
      } catch (e) {
        console.error(e);
        Sentry.captureException(e);
      } finally {
        setIsLoggingIn(false);
      }
    })();
  }, [
    fetchToken, 
    fetchAndStoreAccount
  ]);

  const markStoredAccountAsActive = useCallback(
    account => {
      if (!account) throw new Error('Could not switch to account - no account provided.');

      let storedAccounts = retrieveStoredAccounts();

      const newStoredAccounts = storedAccounts
        .map(a => {
          if (a.data.id === account.data.id) {
            return Object.assign({}, a, { isActive: true });
          } else {
            return Object.assign({}, a, { isActive: false });
          }
        });

      window?.localStorage?.setItem('accounts', JSON.stringify(newStoredAccounts));

      return newStoredAccounts;
    },
    [retrieveStoredAccounts]
  )

  const switchToAccount = useCallback(
    account => {
      Api.cache.store.store = {};
      const storedAccounts = markStoredAccountAsActive(account);
      setAccounts(storedAccounts);
    },
    [
      markStoredAccountAsActive,
      setAccounts
    ]
  )

  const refreshAccounts = useCallback(
    accounts => {
      for (const account of accounts) {
        fetchAndStoreAccount({
          token: account.token,
          makeActive: account.isActive,
          doRedirect: false,
        });  
      }
    }, 
    [fetchAndStoreAccount]
  )

  // localStorage.accounts -> accounts
  useEffect(() => {
    try {
      const accounts = retrieveStoredAccounts();
      setAccounts(accounts);
      refreshAccounts(accounts);
    } catch (e) {
      Sentry.captureException(e); // we're only capturing fatal errors here
      setAccounts([]);
    }
  }, [
    refreshAccounts,
    retrieveStoredAccounts,
  ]);

  // cross-tab sync of account changes
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'accounts') {
        const accounts = retrieveStoredAccounts();
        setAccounts(accounts);
      }
    };
    window?.addEventListener?.('storage', onStorage);
    return () => {
      window?.removeEventListener?.('storage', onStorage);
    };
  }, [retrieveStoredAccounts]);

  const logoutAccount = useCallback(
		account => {
      Api.cache.store.store = {};
      const storedAccounts = removeAccountFromStore(account.token);
      setAccounts(storedAccounts);
    },
		[
      removeAccountFromStore,
      setAccounts
    ]
	);

  return (
    <AuthContext.Provider 
      value={{
        account,
        accounts,
        isFetchingAccount,
        isLoggingIn,
        login,
        setLoginRedirectTo,
        switchToAccount,
        token: exposedToken,
        logoutAccount,
      }}
    >
      { children }
    </AuthContext.Provider >
  );
}

export const withAuth = WrappedComponent => props => {
  const auth = useAuth();
  return <WrappedComponent {...props} {...auth} />;
};

export const useAuth = () => useContext(AuthContext);

export default withRouter(AuthProvider);